import { createAsyncThunk } from '@reduxjs/toolkit';
import Axios from 'axios';

import APIDataProvider, { TApiResponse } from '../../../api/ApiDataProvider';
import { ICourseSubscriptionSession, IStripeCustomerPortalData } from '.';
import { ApiPaths } from '../../../api/APIPaths';
import { TCoursePricingType } from '../CoursesSlice';
import { TUTOPIYA_TENANT_KEY } from '../TenantsSlice';

export type TSubscribeCourse = {
  courseId: string;
  subscriptionType: TCoursePricingType;
  deviceTimezone?: string;
};

export type TCreateFlexibleStripeSession = {
  planId: string;
  userId?: string;
};

export type TCreateSubscriptionStripeSession = {
  planId: string;
  userId?: string;
};

export type TCreateTopUpCourseSession = {
  courseId: string;
  tutorId: string;
  hours: number;
  discountCode?: string;
  isCustom?: boolean;
  includeResourceAccess?: boolean;
  autoTopupHours?: number;
  convertToSGD?: boolean;
};

export type TCreateCourseMonthlySubscriptionSession = {
  courseId: string;
  tutorId: string;
  hours: number;
  isCustom?: boolean;
  includeResourceAccess?: boolean;
  convertToSGD?: boolean;
  // 'month' (default) bills monthly; 'quarter' bills every 3 months.
  billingInterval?: 'month' | 'quarter';
};

export type TCreateCourseBundlePaymentSession = {
  courseListData: Array<{
    courseId: string;
    creditHours: number;
  }>;
};

const apiDataProvider = new APIDataProvider();

export const subscribeCourse = createAsyncThunk(
  '/stripeV2/subscribe-course',
  async (data: TSubscribeCourse) => {
    const path = `${ApiPaths.stripe}/subscribe-course`;

    const response = await apiDataProvider.post<
      TApiResponse<{
        subscriptionType: TCoursePricingType;
        session: ICourseSubscriptionSession;
      }>
    >(path, data);

    return response.data;
  }
);

export const getCourseSubscriptionSession = createAsyncThunk(
  '/stripeV2/course-subscription-session/id',
  async (id: string) => {
    const path = `${ApiPaths.stripe}/course-subscription-session/${id}`;

    const response = await apiDataProvider.get<
      TApiResponse<ICourseSubscriptionSession>
    >(path);

    return response.data;
  }
);

export const createFlexibleStripeSession = createAsyncThunk(
  '/stripeV2/flexible-session/create',
  async (data: TCreateFlexibleStripeSession) => {
    const path = `${ApiPaths.stripe}/flexible-session`;

    const response = await apiDataProvider.post<
      TApiResponse<ICourseSubscriptionSession>
    >(path, data);

    return response.data;
  }
);

export const createSubscriptionStripeSession = createAsyncThunk(
  '/stripeV2/subscription-session/create',
  async (data: TCreateSubscriptionStripeSession) => {
    const path = `${ApiPaths.stripe}/subscription-session`;

    const response = await apiDataProvider.post<
      TApiResponse<ICourseSubscriptionSession>
    >(path, data);

    return response.data;
  }
);

export type TGetStripeCustomerPortalData = {
  /** When true, backend may return a portal session without self-service cancel. */
  billingOnly?: boolean;
  /** Recorded before opening the portal so cancellation can proceed in Stripe. */
  cancellationReason?: string;
  /**
   * Ask backend to disable Stripe's post-cancel feedback survey
   * (reason is already collected in the LP modal).
   */
  disableStripeCancellationFeedback?: boolean;
};

export const getStripeCustomerPortalData = createAsyncThunk(
  '/stripeV2/customer-portal-data/get',
  async (params?: TGetStripeCustomerPortalData) => {
    const path = `${ApiPaths.stripe}/customer-portal-data`;

    if (params?.cancellationReason?.trim()) {
      const reason = params.cancellationReason.trim();
      const portalPayload = {
        cancellationReason: reason,
        disableStripeCancellationFeedback: true,
        collectCancellationReason: false,
      };

      try {
        const response = await apiDataProvider.post<
          TApiResponse<IStripeCustomerPortalData>
        >(path, portalPayload);
        return response.data;
      } catch {
        const query = `?cancellationReason=${encodeURIComponent(
          reason
        )}&disableCancellationFeedback=1&collectCancellationReason=0`;
        const response = await apiDataProvider.get<
          TApiResponse<IStripeCustomerPortalData>
        >(`${path}${query}`);
        return response.data;
      }
    }

    const queryParts: string[] = [];
    if (params?.billingOnly) {
      queryParts.push('billingOnly=1');
    }
    const query = queryParts.length ? `?${queryParts.join('&')}` : '';

    const response = await apiDataProvider.get<
      TApiResponse<IStripeCustomerPortalData>
    >(`${path}${query}`);

    return response.data;
  }
);

export type TCancelLpSubscription = {
  reason: string;
};

export const cancelLpSubscription = createAsyncThunk(
  '/stripeV2/premium/cancel-subscription',
  async (data: TCancelLpSubscription, { rejectWithValue }) => {
    try {
      const path = `${ApiPaths.stripe}/premium/cancel-subscription`;
      const response = await apiDataProvider.post<TApiResponse<unknown>>(
        path,
        data
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export type TRequestLpSubscriptionPause = {
  studentName: string;
  studentEmail: string;
  userId: string;
  pauseUntilDate: string;
  country?: string;
};

export const requestLpSubscriptionPause = createAsyncThunk(
  '/stripeV2/premium/pause-subscription-request',
  async (data: TRequestLpSubscriptionPause, { rejectWithValue }) => {
    try {
      const path = `${ApiPaths.stripe}/premium/pause-subscription-request`;
      const response = await apiDataProvider.post<TApiResponse<unknown>>(
        path,
        data
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const createTopUpCourseSession = createAsyncThunk(
  '/stripeV2/top-up-course',
  async (data: TCreateTopUpCourseSession) => {
    const path = `${ApiPaths.stripe}/top-up-course`;

    const response = await apiDataProvider.post<
      TApiResponse<ICourseSubscriptionSession>
    >(path, data);

    return response.data;
  }
);

export const createCourseMonthlySubscriptionSession = createAsyncThunk(
  '/stripeV2/create-course-monthly-subscription-session',
  async (data: TCreateCourseMonthlySubscriptionSession) => {
    const path = `${ApiPaths.stripe}/create-course-monthly-subscription-session`;

    const response = await apiDataProvider.post<
      TApiResponse<ICourseSubscriptionSession>
    >(path, data);

    return response.data;
  }
);

export const createCourseBundlePaymentSession = createAsyncThunk(
  '/stripeV2/create-course-bundle-payment-session',
  async (data: TCreateCourseBundlePaymentSession) => {
    const path = `${ApiPaths.stripe}/create-course-bundle-payment-session`;

    const response = await apiDataProvider.post<
      TApiResponse<ICourseSubscriptionSession>
    >(path, data);

    return response.data;
  }
);

// Shareable payment links

export type TPaymentLinkSource = 'STUDENT_SHARE';

export type TCreateCourseTopUpPaymentLink = {
  type: 'COURSE_TOPUP';
  userId: string;
  source: TPaymentLinkSource;
  courseId: string;
  tutorId: string;
  hours: number;
  autoTopupHours?: number;
  includeResourceAccess?: boolean;
  convertToSGD?: boolean;
};

export type TCreateCourseMonthlySubscriptionPaymentLink = {
  type: 'COURSE_MONTHLY_SUBSCRIPTION';
  userId: string;
  source: TPaymentLinkSource;
  courseId: string;
  tutorId: string;
  hours: number;
  includeResourceAccess?: boolean;
  convertToSGD?: boolean;
};

export type TCreateCoursePaymentLink = {
  type: 'COURSE';
  userId: string;
  source: TPaymentLinkSource;
  courseId: string;
  subscriptionType: 'ONETIME' | 'SUBSCRIPTION';
  deviceTimezone?: string;
};

export type TCreatePaymentLinkBody =
  | TCreateCourseTopUpPaymentLink
  | TCreateCourseMonthlySubscriptionPaymentLink
  | TCreateCoursePaymentLink;

export const createPaymentLink = createAsyncThunk(
  '/stripeV2/payment-links/create',
  async (data: TCreatePaymentLinkBody, { rejectWithValue }) => {
    try {
      const path = `${ApiPaths.stripe}/payment-links`;

      const response = await apiDataProvider.post<TApiResponse<{ id: string }>>(
        path,
        data
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const checkoutFromPaymentLink = async (
  id: string,
  tenantKey?: string
): Promise<{ url: string }> => {
  const url = `${process.env.REACT_APP_TUTOPIYA_BASE_URL_V2}${ApiPaths.stripe}/checkout-from-link/${id}`;

  const response = await Axios.post<TApiResponse<{ url: string }>>(
    url,
    {},
    {
      headers: {
        'Content-Type': 'application/json',
        'X-Tenant-Key': tenantKey || TUTOPIYA_TENANT_KEY,
      },
    }
  );

  return response.data.data;
};

export type TSendPaymentLink = {
  email?: string;
  whatsapp?: string;
  paymentLink: string;
  packageName: string;
};

export const sendPaymentLink = createAsyncThunk(
  '/stripeV2/payment-links/send',
  async (data: TSendPaymentLink, { rejectWithValue }) => {
    try {
      const path = `${ApiPaths.stripe}/sendPaymentLink`;

      const response = await apiDataProvider.post<TApiResponse<unknown>>(
        path,
        data
      );

      return response.data;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);
